import { Dialog } from '@hai/ui-react';
import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { StreamViewModel } from './stream-models';

interface Props {
  itemsToDelete: StreamViewModel[];
  onApply: (items: StreamViewModel[]) => void;
  onCancel: VoidFunction;
}

export const StreamDeleteConfirmModal: React.FunctionComponent<Props> = ({ itemsToDelete, onApply, onCancel }) => {
  const { t } = useTranslation();

  const handleDelete = (): void => {
    onApply(itemsToDelete);
  };

  const keyDownTargetRef = useRef<HTMLElement | null>(null);
  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter') {
      // Store the element that received the keydown event
      keyDownTargetRef.current = e.target as HTMLElement;
    }
  };

  const handleOnKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    // console.log('keyup', e);
    if (e.key === 'Enter') {
      // Check if keyup target matches keydown target
      const keyUpTarget = e.target as HTMLElement;
      if (keyDownTargetRef.current !== keyUpTarget) {
        keyDownTargetRef.current = null; // Reset for next interaction
        return; // This fix when opening the modal with the keyboard on the DELETE button. The keyUp is registered on the modal's newly focussed CANCEL button
      }

      // Reset the ref after successful match
      keyDownTargetRef.current = null;

      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      if ((e.target as HTMLElement)?.id?.toString().includes('-cancel')) {
        // from cancel button, close modal
        onCancel();
        return;
      }
      if ((e.target as HTMLElement)?.id?.toString().includes('-submit')) {
        // from submit button, dont submit twice
        return;
      }
      // handleDelete(); on DELETE modals dont use Enter to submit by default (if focused on random part of the modal)
    }
  };

  useLayoutEffect(() => {
    const el = document.getElementById('stream-delete-confirm-modal-cancel');
    if (el) {
      el.focus();
      el.classList.add('force-focus-visible'); // Add class to force focus-visible styles
    }

    return () => {
      el?.classList.remove('force-focus-visible');
    };
  }, []);

  return (
    <Dialog
      headline={t('stream.deleteModal.title', { count: itemsToDelete.length })}
      bodyText={t('general.cannotUndo')}
      onClose={onCancel}
      {...{ onKeyUp: handleOnKeyUp, onKeyDown: handleOnKeyDown }}
      show={true}
      buttons={[
        { label: t('general.cancel'), id: 'stream-delete-confirm-modal-cancel' },
        {
          label: t('general.deleteBtn'),
          variant: 'danger',
          onClick: handleDelete,
          id: 'stream-delete-confirm-modal-submit',
        },
      ]}
    />
  );
};
